import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Upload, CheckCircle, AlertCircle, Trash2, Loader2, Eye } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const ComplianceChecklist = ({ transactionId, transactionType, onChange }) => {
  const [pkg, setPkg] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingKey, setUploadingKey] = useState(null);

  const load = async () => {
    if (!transactionType) { setPkg(null); setLoading(false); return; }
    try {
      setLoading(true);
      const [pRes, dRes] = await Promise.all([
        api.get(`/compliance/packages/by-type/${encodeURIComponent(transactionType)}`).catch(() => ({ data: null })),
        api.get(`/transactions/${transactionId}/documents`).catch(() => ({ data: [] })),
      ]);
      setPkg(pRes.data);
      setDocs(dRes.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [transactionId, transactionType]);

  const docFor = (key) => docs.find(d => d.form_key === key);

  const handleUpload = async (form, file) => {
    if (!file) return;
    setUploadingKey(form.key);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('form_key', form.key);
      fd.append('form_name', form.name);
      await api.post(`/transactions/${transactionId}/documents`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Uploaded: ${form.name}`);
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingKey(null);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Remove this document?')) return;
    try {
      await api.delete(`/transactions/${transactionId}/documents/${docId}`);
      toast.success('Document removed');
      await load();
      onChange?.();
    } catch (e) {
      toast.error('Failed to remove');
    }
  };

  const handleView = async (doc) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/transactions/${transactionId}/documents/${doc.id}/download`;
    try {
      // Fetch with auth header, then open blob URL
      const res = await api.get(`/transactions/${transactionId}/documents/${doc.id}/download`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      window.open(blobUrl, '_blank');
    } catch {
      window.open(url, '_blank');
    }
  };

  if (loading) return <div className="text-xs text-gray-500 mt-3">Loading checklist…</div>;

  if (!transactionType) {
    return (
      <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
        Pick a <strong>Transaction Type</strong> to see the required documents checklist.
      </div>
    );
  }

  if (!pkg) {
    return (
      <div className="mt-3 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded p-2">
        No compliance package defined for "{transactionType}". Ask admin to create one.
      </div>
    );
  }

  const forms = pkg.required_forms || [];
  const uploadedKeys = new Set(docs.map(d => d.form_key));
  const requiredForms = forms.filter(f => f.required);
  const requiredUploaded = requiredForms.filter(f => uploadedKeys.has(f.key)).length;
  const pct = requiredForms.length === 0 ? 100 : Math.round((requiredUploaded / requiredForms.length) * 100);

  return (
    <div className="mt-3 space-y-2" data-testid={`compliance-checklist-${transactionId}`}>
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-800">📋 Compliance Docs — {pkg.name}</h4>
        <Badge className={pct === 100 ? 'bg-emerald-100 text-emerald-800 border-emerald-300' : 'bg-amber-100 text-amber-800 border-amber-300'}>
          {requiredUploaded}/{requiredForms.length} required · {pct}%
        </Badge>
      </div>
      <ul className="space-y-1.5">
        {forms.map((f) => {
          const d = docFor(f.key);
          const isUp = uploadingKey === f.key;
          return (
            <li
              key={f.key}
              className={`flex items-center justify-between gap-2 py-2 px-3 rounded border ${
                d ? 'bg-emerald-50 border-emerald-200' : f.required ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'
              }`}
              data-testid={`checklist-item-${f.key}`}
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                {d ? <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" /> : <AlertCircle className={`w-4 h-4 flex-shrink-0 ${f.required ? 'text-red-500' : 'text-gray-400'}`} />}
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">
                    {f.name}
                    {f.required && <span className="text-red-500 ml-1">*</span>}
                  </div>
                  {d && <div className="text-[11px] text-gray-500 truncate">{d.filename} · {d.uploaded_by_name}</div>}
                  {f.notes && !d && <div className="text-[11px] text-gray-500">{f.notes}</div>}
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {d ? (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => handleView(d)} className="h-7 px-2" data-testid={`view-doc-${f.key}`}>
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                    <label className="cursor-pointer">
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50`}>
                        Replace
                      </span>
                      <input type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => handleUpload(f, e.target.files?.[0])} data-testid={`replace-doc-${f.key}`} />
                    </label>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(d.id)} className="h-7 px-2 text-red-600" data-testid={`delete-doc-${f.key}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </>
                ) : (
                  <label className={`cursor-pointer ${isUp ? 'opacity-50 pointer-events-none' : ''}`}>
                    <span className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded bg-[#BCA480] text-white hover:bg-[#a89470] font-medium">
                      {isUp ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} {isUp ? 'Uploading…' : 'Upload'}
                    </span>
                    <input type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => handleUpload(f, e.target.files?.[0])} disabled={isUp} data-testid={`upload-doc-${f.key}`} />
                  </label>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default ComplianceChecklist;
